import React, { useState, useEffect } from 'react';
import { useSearchParams } from "react-router-dom";
import {
  Container, Image, Menu,
  Segment,
  Sidebar, Icon,
  Modal, ModalContent, ModalActions,
  Button,
  Header,
  Message,
  MessageHeader
} from 'semantic-ui-react';
import MyMessagesOverviewPage from './MyMessagesPage';
import QRCodePage from './QRCodePage';
import { useAuth } from "../App";
import { saveOrder } from '../utils/firebase';
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import SettingsPage from './SettingsPage';
import LoadingSpinner from '../components/LoadingSpinner';
import OrderDialog from '../components/dialog/OrderDialog';
import { getPlanLabel, PRICES } from '../utils/business';
import { trackOrderCreatedEvent, trackPageView } from '../utils/analytics';
import { useIsMobile } from '../utils/hooks';
import { LogoutDialog } from '../components/dialog/LogoutDialog';
import { BackgroundContainer } from '../components/BackgroundContainer';

const HomePage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [channel, setChannel] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [active, setActive] = useState('msgs');
  const [orderOpen, setOrderOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const menuItemOnClick = newActive => {
    setSearchParams("?tab=" + newActive);
  };
  const isMobile = useIsMobile();

  useEffect(() => {
    if (loading) { return; }
    if (!user || !user.email) {
      navigate("/");
      return;
    }
    const url = document.location.pathname + document.location.search;
    trackPageView(url);

    // checkQRflow()
    checkActivationFlow()
    if (user?.data?.alias != null) {
      setChannel(user.data.alias)
    }
  }, user);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) {
      setActive(tab);
    }
  }, [searchParams]);

  const checkQRflow = () => {
    if (user.data.plan === 'free_contact' || user.data.plan === 'free_contact') {
      if (!user.data.alias) {
        setActive("qr");
      }
    }
  }

  const checkActivationFlow = () => {
    if (user.data.plan !== 'free' && user.data.activePlan !== user.data.plan) {
      setActive("qr");
    }
  }

  if (!user || !user.email || !user.data) {
    return <LoadingSpinner />
  }

  const renderHeader = () => {
    if (active === 'qr') return 'QR KÓD';
    if (active === 'msgs') return 'MOJE SPRÁVY';
    if (active === 'settings') return 'NASTAVENIA';
  }

  const placeOrder = (order) => {
    const orderId = saveOrder(user.uid, order);
    trackOrderCreatedEvent();
    return orderId;
  }

  const renderActivationTeaser = () => {
    return user.data.plan != 'free' && user.data.activePlan !== user.data.plan && <Message warning>
      <MessageHeader>Aktivuj si svoju verziu!</MessageHeader>
      <p>Aktuálne používaš stále FREE verziu produktu, hoci si si vybral {getPlanLabel(user.data.plan)}.</p>
      <p>Aktivuj si svoju verziu {getPlanLabel(user.data.plan)} a funguj naplno.</p>
      <p>Vyplň záväznú objednávku a po jej potvrdení máš účet aktivovaný.</p>
      <Button color='yellow' onClick={() => setOrderOpen(true)}>AKTIVOVAŤ VERZIU {getPlanLabel(user.data.plan)}</Button>
    </Message>
  }

  const createOrderItem = () => {
    const products = [{
      plan: 'premium',
      id: 1,
      name: 'Aktivácia PRÉMIUM služby KONTAKTUJ',
      description: [
        'Služba kontaktovania s vlastným aliasom k dispozícii na 3 roky',
        'QR nálepka na auto, rozmer 15x13cm',
      ],
      qty: 1,
      price: (PRICES.PREMIUM_TOTAL_PRICE / (1 + PRICES.VAT_RATE)) - PRICES.SHIPPING_FEE,
      shippingFee: PRICES.SHIPPING_FEE,
      vatRate: PRICES.VAT_RATE,
      totalPrice: PRICES.PREMIUM_TOTAL_PRICE
    }];

    //find order by selected plan
    const orderItem = products.filter(a => a.plan === user.data.plan)[0]
    return orderItem;
  }

  return (
    <BackgroundContainer>
      <LogoutDialog
        setLogoutOpen={setLogoutOpen}
        logoutOpen={logoutOpen}
        user={user}
      />
      <>
        {!isMobile ? (
          // --- Desktop Menu ---
          <Menu fixed="top" inverted pointing stackable>
            <Container>
              <Menu.Item as="a" header onClick={() => navigate("/")} style={{ marginTop: '1rem', marginBottom: 12 }}>
                <Image
                  size="small"
                  src="../landing/assets/img/navbar-logo2.svg"
                  style={{ marginRight: '1.5em' }}
                />
              </Menu.Item>
              <Menu.Item
                as="a"
                active={active === 'msgs'}
                onClick={() => menuItemOnClick('msgs')}
              >
                Moje správy
              </Menu.Item>
              <Menu.Item
                as="a"
                active={active === 'qr'}
                onClick={() => menuItemOnClick('qr')}
              >
                QR kód
              </Menu.Item>
              <Menu.Item
                as="a"
                active={active === 'settings'}
                onClick={() => menuItemOnClick('settings')}
              >
                Nastavenia
              </Menu.Item>
              <div style={{ width: "100%", display: 'flex', justifyContent: 'end' }}>
                <Menu.Item
                  as="a"
                  onClick={() => {
                    setLogoutOpen(true);
                  }}
                >
                  Odhlásiť
                </Menu.Item>
                <Menu.Item
                  as="a"
                  header
                >
                  Prihlásený: {user.email}
                </Menu.Item>
              </div>
            </Container>
          </Menu>
        ) : (
          <>
            {/* --- Mobile Trigger --- */}
            <Menu style={{ marginRight: '1rem', marginTop: '1rem' }} fixed="top" inverted pointing secondary>
              <Container>
                <Menu.Item position="right">
                  <Image
                    size="small"
                    src="../landing/assets/img/navbar-logo2.svg"
                    style={{ marginRight: '1rem' }}
                    onClick={() => navigate("/")}
                  />
                  <Button icon inverted onClick={() => setSidebarOpened(true)}>
                    <Icon name="bars" />
                  </Button>
                </Menu.Item>
              </Container>
            </Menu>

            {/* --- Sidebar Snackbar --- */}
            <Sidebar
              as={Menu}
              animation="overlay"
              onHide={() => setSidebarOpened(false)}
              vertical
              visible={sidebarOpened}
              inverted
            >
              <Menu.Item as="a" header>
                <Segment inverted>
                  Prihlásený: {user.email}
                </Segment>
              </Menu.Item>

              <Menu.Item
                as="a"
                active={active === 'msgs'}
                onClick={() => {
                  menuItemOnClick('msgs');
                  setSidebarOpened(false);
                }}
              >
                Moje správy
              </Menu.Item>
              <Menu.Item
                as="a"
                active={active === 'qr'}
                onClick={() => {
                  menuItemOnClick('qr');
                  setSidebarOpened(false);
                }}
              >
                QR kód
              </Menu.Item>
              <Menu.Item
                as="a"
                active={active === 'settings'}
                onClick={() => {
                  menuItemOnClick('settings');
                  setSidebarOpened(false);
                }}
              >
                Nastavenia
              </Menu.Item>
              <Menu.Item
                as="a"
                active={active === 'logout'}
                onClick={() => {
                  setLogoutOpen(true);
                }}
              >
                Odhlásiť
              </Menu.Item>
            </Sidebar>
          </>
        )}
      </>

      <div style={{ height: '80vh' }}>
        <Container textAlign='center'>
          {renderActivationTeaser()}
          <Header style={{ color: 'white', margin: '2rem', marginTop: '1rem' }}>{renderHeader()}</Header>
        </Container>
        {active === 'msgs' && channel &&
          <div text id="msgsContainer" >
            <MyMessagesOverviewPage channel={channel} senderId={searchParams.get("sender")} />
          </div>
        }

        {active === 'qr' &&
          <Container>
            <QRCodePage />
          </Container>
        }

        {active === 'settings' &&
          <Container>
            <SettingsPage />
          </Container>
        }
      </div>

      <OrderDialog open={orderOpen} orderItem={createOrderItem()} onClose={() => setOrderOpen(false)} onComplete={(order) => placeOrder(order)} />
    </BackgroundContainer>
  );
};

export default HomePage;